'use client';

import { fetchRankingGoodsList } from '@/api/product';
import { env } from '@/utils/env';
import { mapRuntimeBestsellerProduct, resolveProductRankingTagType } from './runtimeData';
import type { BestsellerProduct, BestsellerRankingTab } from './types';

export const RANKING_LOAD_BATCH_SIZE = 18;

export interface RankingProductPage {
  products: BestsellerProduct[];
  hasMore: boolean;
  total?: number;
}

export async function loadRankingProductPage(
  tab: BestsellerRankingTab,
  page: number,
): Promise<RankingProductPage> {
  if (!tab.productGroupId) {
    return { products: [], hasMore: false, total: 0 };
  }

  const response = await fetchRankingGoodsList({
    group_id: tab.productGroupId,
    ranking_tag: resolveProductRankingTagType(tab.productLabel),
    page,
    limit: RANKING_LOAD_BATCH_SIZE,
  });
  if (response.code !== 200 || !response.data) {
    throw new Error(`Failed to load bestseller ranking page ${page}`);
  }

  const list = response.data.list || [];
  const products = list.map((product, index) => {
    const rank = (page - 1) * RANKING_LOAD_BATCH_SIZE + index + 1;
    const showProductLabel = Boolean(tab.productLabel) && tab.showProductLabel !== false;
    const mappedProduct = mapRuntimeBestsellerProduct(
      product as unknown as Record<string, unknown>,
      rank,
      env.NEXT_PUBLIC_S3_URL,
      tab.productLabel,
      showProductLabel,
    );
    const topProduct = tab.topProducts?.find((item) => item.rank === rank);
    return {
      ...mappedProduct,
      ...(topProduct
        ? {
            modelImage: topProduct.pcInfluencerImage || mappedProduct.modelImage,
            mobileModelImage: topProduct.wapInfluencerImage || mappedProduct.mobileModelImage,
          }
        : {}),
      labelIcon: showProductLabel ? tab.icon : undefined,
    };
  });
  const responseTotal = Number(response.data.total ?? response.data.all_total);
  const total = Number.isFinite(responseTotal)
    ? Math.max(responseTotal, products.length)
    : undefined;

  return {
    products,
    total,
    hasMore:
      total !== undefined && total > 0
        ? page * RANKING_LOAD_BATCH_SIZE < total
        : list.length >= RANKING_LOAD_BATCH_SIZE,
  };
}
